import { persistentAtom } from '@nanostores/persistent';
import { z } from '@customerio/hydra-validation';
import { isProductionDevHelperEnvironment } from './store';

// The wallet lives in the billing backend and has no write endpoint, so the simulator rewrites
// the wallet in this browser's `GET /usage` responses instead. Source: services `domains/wallet/meter.go`.
export const WALLET_METERS = [
  'email_sends',
  'webhook_sends',
  'active_profiles',
  'inbox_previews',
  'ai_credits',
  'agent_usage',
] as const;

export type WalletMeter = (typeof WALLET_METERS)[number];

// The meters whose use is typed in on its own. Email sends and active profiles take the usage figures
// the simulation already sets, so one figure drives both the usage pages and the wallet.
export const USAGE_INPUT_METERS = ['webhook_sends', 'inbox_previews', 'ai_credits', 'agent_usage'] as const;

// The price of one unit in credits. Source: services `domains/wallet/pricing.go`.
const CREDITS_PER_UNIT = {
  email_sends: 1_500,
  webhook_sends: 4_000,
  active_profiles: 20_000,
  inbox_previews: 250_000,
  ai_credits: 100,
  agent_usage: 100,
} satisfies Record<WalletMeter, number>;

// Source: services `domains/wallet/credits.go`.
const CREDITS_PER_DOLLAR = 1_000_000;

// These meters draw only from their own grants. Source: services `domains/wallet/meter.go`.
const GRANT_ONLY_METERS: ReadonlySet<WalletMeter> = new Set(['agent_usage']);

const count = z.number().int().min(0);

export const STORAGE_ZONES = ['ok', 'soft', 'hard'] as const;

const grantSchema = z.object({ amount: count, remaining: count });

const workspaceSchema = z.object({ id: z.number().int(), name: z.string() });

export type SimulatedWorkspace = z.infer<typeof workspaceSchema>;

// Email sends that grow with the day of the billing period, so the spend per day, and with it the
// runway, stays where the preset put it.
const runwaySchema = z.object({
  days: z.number().int().min(1),
  sendsPerDay: z.number().min(0),
  periodStart: z.number().int().min(1),
});

const walletSimulationSchema = z.object({
  accountId: z.number().int(),
  balanceDollars: z.number().min(0),
  hasPurchase: z.boolean(),
  grants: z.partialRecord(z.enum(WALLET_METERS), grantSchema),
  // Units used this period, for the meters with no usage figure of their own. A missing meter used none.
  // The default keeps a simulation saved before this field existed.
  meterUsage: z.partialRecord(z.enum(USAGE_INPUT_METERS), count).default({}),
  // The workspace the wallet usage is charged to. Null charges it to no workspace.
  workspace: workspaceSchema.nullable().default(null),
  // Usage figures from the same response; null keeps the real one. Sends set the runway, profiles the free limit.
  billableMessagesSent: count.nullable(),
  runway: runwaySchema.nullable(),
  monthlyActiveUsers: count.nullable(),
  storedProfiles: count.nullable(),
  // The storage zone on `/billing/wallet/usage`; null keeps the real one. The default keeps a simulation
  // saved before this field existed.
  storageZone: z.enum(STORAGE_ZONES).nullable().default(null),
});

export type WalletSimulation = z.infer<typeof walletSimulationSchema>;
export type SimulatedRunway = z.infer<typeof runwaySchema>;

export function parseWalletSimulation(value: unknown) {
  return walletSimulationSchema.safeParse(value);
}

function decodeSimulation(raw: string): WalletSimulation | null {
  if (isProductionDevHelperEnvironment()) return null;
  try {
    const parsed = parseWalletSimulation(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

// No cross-tab listener: every API request reads this atom, and a storage listener would be added
// and removed around each read. Applying or clearing reloads the page, and other tabs read it on reload.
export const $walletSimulation = persistentAtom<WalletSimulation | null>('dev-helper-wallet-simulation', null, {
  encode: JSON.stringify,
  decode: decodeSimulation,
  listen: false,
});

export function setWalletSimulation(simulation: WalletSimulation | null) {
  $walletSimulation.set(simulation);
}

const SECONDS_PER_DAY = 86_400;

/** The sends that keep a runway on track today. Day 1 is the period's first day, as the app counts it. */
export function runwaySends(runway: SimulatedRunway, now = new Date()) {
  const dayNumber = Math.max(Math.floor((now.getTime() / 1000 - runway.periodStart) / SECONDS_PER_DAY) + 1, 1);
  return Math.ceil(runway.sendsPerDay * dayNumber);
}

function billableMessagesSent(simulation: WalletSimulation, now: Date) {
  if (simulation.runway != null) return runwaySends(simulation.runway, now);
  return simulation.billableMessagesSent;
}

/** Units of the meter used this period. A usage figure left to the real one counts as none here. */
function meterUsed(simulation: WalletSimulation, meter: WalletMeter, now: Date): number {
  if (meter === 'email_sends') return billableMessagesSent(simulation, now) ?? 0;
  if (meter === 'active_profiles') return simulation.monthlyActiveUsers ?? 0;
  const inputMeter = USAGE_INPUT_METERS.find((candidate) => candidate === meter);
  if (inputMeter == null) return 0;
  return simulation.meterUsage[inputMeter] ?? 0;
}

export type SimulatedMeter = {
  meter: WalletMeter;
  grantTotal: number;
  grantUsed: number;
  grantRemaining: number;
  balanceUsed: number;
  balanceCredits: number;
  totalUsed: number;
};

/**
 * Each priced meter's use this period. The grant pays first, for at most what it has spent
 * (amount less remaining). The balance pays for the rest, unless the meter draws only from its grant.
 */
export function simulatedMeters(simulation: WalletSimulation, now = new Date()): SimulatedMeter[] {
  return WALLET_METERS.flatMap((meter) => {
    const creditsPerUnit = CREDITS_PER_UNIT[meter];
    const used = meterUsed(simulation, meter, now);
    const grant = simulation.grants[meter];
    const grantTotal = grant?.amount ?? 0;
    const grantRemaining = grant?.remaining ?? 0;
    const grantSpent = Math.max(grantTotal - grantRemaining, 0);
    const grantUsed = Math.min(used, grantSpent);
    const balanceUsed = GRANT_ONLY_METERS.has(meter) ? 0 : used - grantUsed;

    return [
      {
        meter,
        grantTotal,
        grantUsed,
        grantRemaining,
        balanceUsed,
        balanceCredits: balanceUsed * creditsPerUnit,
        totalUsed: grantUsed + balanceUsed,
      },
    ];
  });
}

/** US dollars the meters drew from the balance this period. */
export function simulatedSpendDollars(simulation: WalletSimulation, now = new Date()): number {
  const credits = simulatedMeters(simulation, now).reduce((sum, meter) => sum + meter.balanceCredits, 0);
  return credits / CREDITS_PER_DOLLAR;
}

// Far enough out that no simulated bucket expires mid-session.
const SIMULATED_ENDS_AT = '2099-01-01T00:00:00Z';

function toWallet(simulation: WalletSimulation, now: Date) {
  const startsAt = now.toISOString();
  const balance = simulation.balanceDollars;
  const spent = simulatedSpendDollars(simulation, now);
  const showDeposit = simulation.hasPurchase || balance > 0;
  const deposits = showDeposit
    ? [
        {
          id: -1,
          kind: simulation.hasPurchase ? 'purchase' : 'goodwill',
          amount: balance + spent,
          remaining: balance,
          starts_at: startsAt,
          ends_at: SIMULATED_ENDS_AT,
        },
      ]
    : [];
  const grants = Object.fromEntries(
    WALLET_METERS.flatMap((meter, index) => {
      const grant = simulation.grants[meter];
      if (grant == null) return [];
      const bucket = { id: -(index + 2), ...grant, starts_at: startsAt, ends_at: SIMULATED_ENDS_AT };
      return [[meter, [bucket]]];
    }),
  );

  return {
    balance,
    total_spent: spent,
    has_funded: simulation.hasPurchase,
    buckets: { deposits, grants },
  };
}

const usageResponseSchema = z.looseObject({
  account_usage: z.looseObject({ environment_usages: z.array(z.looseObject({})).nullish() }),
});

type UsageResponse = z.infer<typeof usageResponseSchema>;

// Stored profiles are read from the account total or, with an unbilled workspace, the sum over
// workspaces. Putting them all on the first workspace makes both read the simulated figure.
function storedProfileFields(usage: UsageResponse['account_usage'], storedProfiles: number) {
  const environments = usage.environment_usages?.map((environment, index) => ({
    ...environment,
    customers_total: index === 0 ? storedProfiles : 0,
  }));
  return { customers_total: storedProfiles, ...(environments != null && { environment_usages: environments }) };
}

/** Rewrites the wallet, and any usage the simulation sets, in a `/usage` or `/usage_v2` body. */
export function applyWalletSimulation(body: UsageResponse, simulation: WalletSimulation, now = new Date()) {
  const usage = body.account_usage;
  const sends = billableMessagesSent(simulation, now);
  return {
    ...body,
    account_usage: {
      ...usage,
      wallet: toWallet(simulation, now),
      ...(sends != null && { billable_messages_sent: sends }),
      ...(simulation.monthlyActiveUsers != null && { monthly_active_users: simulation.monthlyActiveUsers }),
      ...(simulation.storedProfiles != null && storedProfileFields(usage, simulation.storedProfiles)),
    },
  } satisfies UsageResponse;
}

const walletUsageResponseSchema = z.looseObject({
  balance: z.looseObject({}),
  meters: z.record(z.string(), z.looseObject({})),
  storage: z.looseObject({ stored: z.number(), zone: z.enum(STORAGE_ZONES) }).nullish(),
});

type WalletUsageResponse = z.infer<typeof walletUsageResponseSchema>;

// The simulated stored profiles are the whole stored count here, so the account's real objects do not add to it.
function simulatedStorage(storage: WalletUsageResponse['storage'], simulation: WalletSimulation) {
  if (simulation.storedProfiles == null && simulation.storageZone == null) return {};
  return {
    storage: {
      stored: simulation.storedProfiles ?? storage?.stored ?? 0,
      zone: simulation.storageZone ?? storage?.zone ?? 'ok',
    },
  };
}

/**
 * Rewrites the balance, every priced meter and the storage the simulation sets in a
 * `/billing/wallet/usage` body. A meter's use goes on one entry, for the simulation's workspace.
 */
export function applyWalletUsageSimulation(body: WalletUsageResponse, simulation: WalletSimulation, now = new Date()) {
  const meters = Object.fromEntries(
    simulatedMeters(simulation, now).map((meter) => {
      const balanceCost = meter.balanceCredits / CREDITS_PER_DOLLAR;
      const environments =
        meter.totalUsed > 0
          ? [
              {
                environment_id: simulation.workspace?.id ?? null,
                name: simulation.workspace?.name ?? null,
                grant_used: meter.grantUsed,
                balance_used: meter.balanceUsed,
                balance_cost: balanceCost,
                total_used: meter.totalUsed,
              },
            ]
          : [];
      return [
        meter.meter,
        {
          grant_total: meter.grantTotal,
          grant_used: meter.grantUsed,
          grant_remaining: meter.grantRemaining,
          balance_used: meter.balanceUsed,
          balance_cost: balanceCost,
          total_used: meter.totalUsed,
          environments,
        },
      ];
    }),
  );

  return {
    ...body,
    balance: { ...body.balance, used: simulatedSpendDollars(simulation, now), remaining: simulation.balanceDollars },
    meters,
    ...simulatedStorage(body.storage, simulation),
  } satisfies WalletUsageResponse;
}

const USAGE_PATH = /^\/v1\/accounts\/(\d+)\/usage(?:_v2)?$/;
const WALLET_USAGE_PATH = /^\/v1\/accounts\/(\d+)\/billing\/wallet\/usage$/;

type SimulatedRoute = { kind: 'usage' | 'wallet-usage'; accountId: number };

/** The route and account of a GET the simulator rewrites, or null for any other request. */
function simulatedRoute(input: RequestInfo | URL, init: RequestInit | undefined): SimulatedRoute | null {
  const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
  if (method.toUpperCase() !== 'GET') return null;
  const href = input instanceof Request ? input.url : String(input);
  const pathname = new URL(href, window.location.origin).pathname;

  const usage = USAGE_PATH.exec(pathname);
  if (usage != null) return { kind: 'usage', accountId: Number(usage[1]) };
  const walletUsage = WALLET_USAGE_PATH.exec(pathname);
  if (walletUsage != null) return { kind: 'wallet-usage', accountId: Number(walletUsage[1]) };
  return null;
}

/** The body the simulation makes of a response, or null to pass the response through. */
async function simulatedBody(route: SimulatedRoute, response: Response, simulation: WalletSimulation) {
  let body;
  try {
    body = await response.clone().json();
  } catch {
    return null;
  }

  if (route.kind === 'usage') {
    const parsed = usageResponseSchema.safeParse(body);
    return parsed.success ? applyWalletSimulation(parsed.data, simulation) : null;
  }
  const parsed = walletUsageResponseSchema.safeParse(body);
  return parsed.success ? applyWalletUsageSimulation(parsed.data, simulation) : null;
}

/** Rewrites the wallet in the simulated account's usage responses and passes everything else through. */
export const walletSimulatingFetch: typeof globalThis.fetch = async (input, init) => {
  const response = await globalThis.fetch(input, init);
  const simulation = $walletSimulation.get();
  if (simulation == null || !response.ok) return response;
  const route = simulatedRoute(input, init);
  if (route == null || route.accountId !== simulation.accountId) return response;

  const body = await simulatedBody(route, response, simulation);
  if (body == null) return response;
  return Response.json(body, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
};

/** The fetch for `hydraUiApi`. Production builds never simulate, so they get the plain fetch. */
export const walletSimulatorFetch = isProductionDevHelperEnvironment() ? undefined : walletSimulatingFetch;
